import {
  WishGoalCards,
  type RetirementProvisionSelectionSupportAnswer,
  type RetirementProvisionSelectionSupportStep,
} from '@dvag/dfs-ui-blocks/components';
import { useAutosave } from '@dvag/dfs-ui-blocks/hooks/useAutosave';
import { UpdateManyResources } from '@dvag/dfs-ui-blocks/hooks/useAutosaveHelper';
import i18next from 'i18next';
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { object, number, string, boolean } from 'yup';
import { PersonWishGoalDto, RetirementImportance } from 'graphql/generates';

import { useNotification } from 'utils/notification/NotificationContext';
import { useContinuation } from 'utils/useContinuation';
import { FieldType } from './constant';
import { useReadOnlyMode } from 'hooks/useReadOnlyMode';

import { useUpdateManyPersonWishGoals } from './service/useUpdateManyPersonWishGoals';
import { TopDisplayType } from './type';
import { mandatoryFieldMessage } from '../../utils/validation';

interface WishGoalCardsMapProps {
  personId: string;
  initialWgList: PersonWishGoalDto[];
  topDisplayType: TopDisplayType;
  setIsDirty: (isDirty: boolean) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  onWgListChange: (wgList: PersonWishGoalDto[]) => void;
  onContinuationRequest: ReturnType<typeof useContinuation>['onContinuationRequest'];
  showRetirementProvisionSelectionSupport?: boolean;
}

const retirementSelectionSupportFieldMap = {
  fundedSavingsPhase: 'retirementSubsidizedSavings',
  guarantees: 'retirementGuarantees',
  lifelongPension: 'retirementLifelongPension',
  flexibility: 'retirementFlexibility',
  lowTaxesInRetirement: 'retirementLowTaxes',
  freeInheritance: 'retirementFreeInheritance',
  homeOwnership: 'retirementHomeOwnership',
  workforceProtection: 'retirementEarningCapacityProtection',
} as const satisfies Record<RetirementProvisionSelectionSupportStep, keyof PersonWishGoalDto>;

const retirementSelectionSupportAnswerMap = {
  unimportant: RetirementImportance.Unimportant,
  neutral: RetirementImportance.Neutral,
  important: RetirementImportance.Important,
} as const satisfies Record<RetirementProvisionSelectionSupportAnswer, RetirementImportance>;

const retirementSelectionSupportFields = Object.values(retirementSelectionSupportFieldMap);

enum SelectionSupportStatus {
  Idle = 'idle',
  Unchanged = 'unchanged',
  Pending = 'pending',
  Saved = 'saved',
  Notified = 'notified',
}

export const WishGoalCardsMap = ({
  personId,
  initialWgList,
  topDisplayType,
  setIsDirty,
  setIsSubmitting,
  onWgListChange,
  onContinuationRequest,
  showRetirementProvisionSelectionSupport,
}: WishGoalCardsMapProps) => {
  const updateWg = useUpdateManyPersonWishGoals(personId);
  const readOnly = useReadOnlyMode();
  const [trackedWishGoalsIds, setTrackedWishGoalsIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setTrackedWishGoalsIds(new Set(initialWgList.filter((p) => p.id).map((wg) => wg.wishGoalId)));
  }, [initialWgList]);
  const { showError, showSuccess } = useNotification();
  const initialRetirementProvision = initialWgList.find((wg) => wg.name === 'retirementProvision');
  const selectionSupportRef = useRef<{
    firstAttempt: boolean;
    closed: boolean;
    status: SelectionSupportStatus;
  }>({
    firstAttempt: retirementSelectionSupportFields.every(
      (field) => initialRetirementProvision?.[field] == null,
    ),
    closed: false,
    status: SelectionSupportStatus.Idle,
  });

  const notifySelectionSupportSaved = (resources: PersonWishGoalDto[]) => {
    const state = selectionSupportRef.current;

    if (
      state.status !== SelectionSupportStatus.Saved &&
      !(state.status === SelectionSupportStatus.Unchanged && !state.firstAttempt)
    ) {
      return;
    }

    if (state.firstAttempt) {
      const retirementProvision = resources.find((wg) => wg.name === 'retirementProvision');
      if (
        !retirementSelectionSupportFields.every((field) => retirementProvision?.[field] != null)
      ) {
        return;
      }
    } else if (!state.closed) {
      return;
    }

    state.status = SelectionSupportStatus.Notified;
    showSuccess({ title: i18next.t('wishesAndGoals.selectionSupport.saved') });
  };

  const resourceSchema = object().shape({
    wishGoalId: string().required(mandatoryFieldMessage),
    name: string().required(mandatoryFieldMessage),
    id: string().nullable(),
    personId: string().nullable(),
    isDesired: boolean().nullable(),
    order: number().min(1).max(10).nullable(),
    comment: string().nullable(),
  });

  const { register, setValue, flushChanges, triggerValidation, move, getAllResources } =
    useAutosave<PersonWishGoalDto>({
      data: initialWgList,
      updateManyResources: readOnly
        ? undefined
        : (updateWg.mutateAsync as UpdateManyResources<PersonWishGoalDto>),
      onIsDirtyChange: (isDirty) => {
        setIsDirty(isDirty);
      },
      onIsSubmittingChange: (isSubmitting) => {
        setIsSubmitting(isSubmitting);
      },
      onRequestResponse: (data) => {
        const wgMap = new Map(getAllResources().map((res) => [res.wishGoalId, res]));

        data.forEach(({ request, response }) => {
          if (response) {
            const oldWg = wgMap.get(response.wishGoalId);
            if (oldWg) {
              wgMap.set(response.wishGoalId, {
                ...oldWg,
                id: response.id,
                personId: response.personId,
              });
            }

            const state = selectionSupportRef.current;
            if (
              state.status === SelectionSupportStatus.Pending &&
              request.name === 'retirementProvision' &&
              oldWg &&
              retirementSelectionSupportFields.every(
                (field) => (request[field] ?? null) === (oldWg[field] ?? null),
              )
            ) {
              state.status = SelectionSupportStatus.Saved;
            }
          }
        });
        onWgListChange([...wgMap.values()]);
        notifySelectionSupportSaved(getAllResources());
      },
      resourceIdFieldName: 'wishGoalId',
      resourceSchema,
    });

  useEffect(() => {
    onContinuationRequest(() => {
      flushChanges();
      triggerValidation();
    });
  }, [flushChanges, onContinuationRequest, triggerValidation]);

  const [, startTransition] = useTransition();

  const setNewValue = useCallback(
    (fieldName: string, fieldValue: string | number | boolean, resourceIndex: number) => {
      startTransition(() => setValue(fieldName, fieldValue, resourceIndex));
      onWgListChange(getAllResources());
    },
    [getAllResources, onWgListChange, setValue],
  );

  const setTrackedIndex = useCallback(
    (resourceIndex: number) => {
      const { wishGoalId } = getAllResources()[resourceIndex];
      setTrackedWishGoalsIds((wishGoalIds) => new Set([...wishGoalIds, wishGoalId]));
    },
    [getAllResources],
  );

  const onCommentChange = (value: string, resourceIndex: number) => {
    setNewValue(FieldType.comment, value, resourceIndex);
    setNewValue(FieldType.order, resourceIndex + 1, resourceIndex);
    setTrackedIndex(resourceIndex);

    if (!getAllResources()[resourceIndex].id) {
      setNewValue(FieldType.id, Math.random(), resourceIndex);
    }
  };

  const onRetirementProvisionSelectionSupportAnswer = useCallback(
    (
      step: RetirementProvisionSelectionSupportStep,
      answer: RetirementProvisionSelectionSupportAnswer,
      resourceIndex: number,
    ) => {
      const field = retirementSelectionSupportFieldMap[step];
      const value = retirementSelectionSupportAnswerMap[answer];
      const state = selectionSupportRef.current;

      if (state.closed) {
        if (state.status !== SelectionSupportStatus.Idle) {
          state.firstAttempt = false;
        }
        state.closed = false;
        if (state.status !== SelectionSupportStatus.Pending) {
          state.status = SelectionSupportStatus.Idle;
        }
      }

      if (getAllResources()[resourceIndex][field] !== value) {
        state.status = SelectionSupportStatus.Pending;
      } else if (state.status === SelectionSupportStatus.Idle) {
        state.status = SelectionSupportStatus.Unchanged;
      }

      setNewValue(field, value, resourceIndex);
    },
    [getAllResources, setNewValue],
  );

  const moveCard = useCallback(
    (activeIdIndex: number, overIdIndex: number) => {
      move(activeIdIndex, overIdIndex);
      const start = activeIdIndex < overIdIndex ? activeIdIndex : overIdIndex;
      const end = activeIdIndex < overIdIndex ? overIdIndex : activeIdIndex;
      const moveBack = activeIdIndex > overIdIndex;

      for (let i = start; i <= end; i += 1) {
        const { wishGoalId } = getAllResources()[i];
        if (
          (moveBack && i === start) ||
          (!moveBack && i === end) ||
          trackedWishGoalsIds.has(wishGoalId)
        ) {
          setTrackedIndex(i);
          setNewValue(FieldType.order, i + 1, i);

          if (!getAllResources()[i].id) {
            setNewValue(FieldType.id, Math.random(), i);
          }
        }
      }
    },
    [move, getAllResources, trackedWishGoalsIds, setTrackedIndex, setNewValue],
  );

  const onDesireChange = useCallback(
    (isDesired: boolean, resourceIndex: number) => {
      setTrackedIndex(resourceIndex);

      setNewValue(FieldType.isDesired, isDesired, resourceIndex);
      setNewValue(FieldType.order, resourceIndex + 1, resourceIndex);
      const wgList = getAllResources();
      const gapCandidateIndex = wgList.findIndex((p) => !p.isDesired);

      const hasGap =
        gapCandidateIndex !== -1 && wgList.slice(gapCandidateIndex).some((p) => p.isDesired);

      const destinationIndex = wgList.filter((p) => p.isDesired).length + (isDesired ? -1 : 0);
      if (hasGap && destinationIndex !== resourceIndex && destinationIndex < wgList.length) {
        moveCard(resourceIndex, destinationIndex);
      }
    },
    [setTrackedIndex, setNewValue, getAllResources, moveCard],
  );

  const onOrderChange = useCallback(
    (activeId: string, overId: string) => {
      const wgList = getAllResources();
      const activeIdIndex = wgList.findIndex((wg) => wg.wishGoalId === activeId);
      let overIdIndex = wgList.findIndex((wg) => wg.wishGoalId === overId);
      const checkedFieldsLength = wgList.filter((p) => p.isDesired === true).length;
      if (!wgList[activeIdIndex].isDesired && overIdIndex < checkedFieldsLength) {
        overIdIndex = checkedFieldsLength;
      }
      if (wgList[activeIdIndex].isDesired === true && overIdIndex >= checkedFieldsLength) {
        overIdIndex = checkedFieldsLength - 1;
      }

      moveCard(activeIdIndex, overIdIndex);
    },
    [getAllResources, moveCard],
  );

  return (
    <WishGoalCards
      showRetirementProvisionSelectionSupport={showRetirementProvisionSelectionSupport}
      showError={showError}
      list={getAllResources()}
      topDisplayType={topDisplayType}
      onCommentChange={onCommentChange}
      onDesireChange={onDesireChange}
      onOrderChange={onOrderChange}
      onRetirementProvisionSelectionSupportAnswer={onRetirementProvisionSelectionSupportAnswer}
      onRetirementProvisionSelectionSupportClose={() => {
        selectionSupportRef.current.closed = true;
        notifySelectionSupportSaved(getAllResources());
      }}
      register={register}
      readOnly={readOnly}
    />
  );
};
